class ProductAddons extends HTMLElement {
  get product() {
    if (!this._product) {
      const jsonEl = this.querySelector("[data-product-json]");
      if (jsonEl?.textContent) {
        try {
          this._product = JSON.parse(jsonEl.textContent.trim());
        } catch (e) {
          console.error("Failed to parse product JSON:", e);
        }
      }
    }
    return this._product;
  }

  async fetchMainProductDetails() {
    const product = this.product;
    if (!product?.id) {
      return null;
    }

    const apiUrl = this.dataset.storefrontApiUrl;
    const token = this.dataset.storefrontApiToken;

    if (!apiUrl || !token) {
      console.error("Storefront API URL and token are required");
      return null;
    }

    const productId = typeof product.id === "string" && product.id.startsWith("gid://")
      ? product.id
      : `gid://shopify/Product/${product.id}`;

    // Define metafield identifiers to fetch from Storefront API
    const metafieldIdentifiers = [
      { namespace: "custom", key: "sub_category_name" },
      { namespace: "custom", key: "att_nominal_width" }
    ];


    const query = `
      query getProductDetails($id: ID!, $metafieldIdentifiers: [HasMetafieldsIdentifier!]!) {
        product(id: $id) {
          id
          title
          handle
          description
          vendor
          productType
          tags
          metafields(identifiers: $metafieldIdentifiers) {
            key
            namespace
            value
          }
        }
      }
    `;

    const variables = {
      id: productId,
      metafieldIdentifiers: metafieldIdentifiers
    };

    try {

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": token,
        },
        body: JSON.stringify({ query, variables }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const { data, errors } = await response.json();

      if (errors) {
        console.error("[ProductAddons] Storefront API errors:", errors);
        return null;
      }

      const productDetails = data?.product;
      if (productDetails) {
        this.product.tags = productDetails?.tags ?? [];

      }

      return productDetails;
    } catch (error) {
      console.error("[ProductAddons] Error fetching product details:", error);
      return null;
    }
  }

  getProductRulesUrl() {
    return (
      this.dataset.productRulesUrl ||
      new URL("product-rules.json", window.location.origin).href
    );
  }
  
  getZipcodesUrl() {
    return (
      this.dataset.zipcodesUrl ||
      new URL("zipcodes.json", window.location.origin).href
    );
  }

  getZipcodesUrlLocal() {
    return (
      this.dataset.zipcodesUrlLocal ||
      new URL("zipcodes-local.json", window.location.origin).href
    );
  }

  getZipcodesUrlWa() {
    return (
      this.dataset.zipcodesUrlWa ||
      new URL("wa-zipcodes.json", window.location.origin).href
    );
  }

  async getZipcodes(isLocal = false) {
    const cached = sessionStorage.getItem(isLocal ? "zipcodes-local" : "zipcodes");
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch {
        sessionStorage.removeItem(isLocal ? "zipcodes-local" : "zipcodes");
      }
    }

    try {
      const response = await fetch(isLocal ? this.getZipcodesUrlLocal() : this.getZipcodesUrl());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      sessionStorage.setItem(isLocal ? "zipcodes-local" : "zipcodes", JSON.stringify(data));
      return data;
    } catch (error) {
      console.error("Error fetching zipcodes:", error);
      return null;
    }
  }

  async getZipcodesWa() {
    const cached = sessionStorage.getItem("zipcodes-wa");
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch {
        sessionStorage.removeItem("zipcodes-wa");
      }
    }

    try {
      const response = await fetch(this.getZipcodesUrlWa());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      sessionStorage.setItem("zipcodes-wa", JSON.stringify(data));
      return data;
    } catch (error) {
      console.error("Error fetching WA zipcodes:", error);
      return null;
    }
  }

  async getProductRules(isLocal = false) {
    const cached = sessionStorage.getItem(isLocal ? "product-rules-local" : "product-rules");
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch {
        sessionStorage.removeItem(isLocal ? "product-rules-local" : "product-rules");
      }
    }

    try {
      const response = await fetch(isLocal ? this.getProductRulesUrlLocal() : this.getProductRulesUrl());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      sessionStorage.setItem(isLocal ? "product-rules-local" : "product-rules", JSON.stringify(data));
      return data;
    } catch (error) {
      console.error("Error fetching product rules:", error);
      return null;
    }
  }

  getProductRulesUrlLocal() {
    return (
      this.dataset.productRulesUrlLocal ||
      new URL("product-rules-local.json", window.location.origin).href
    );
  }

  initZipCodeInput() {
    const zipInput = this.querySelector("[data-zip-input]");
    if (!zipInput) return;

    const zipWrapper = zipInput.closest(".product-addons__zip-wrapper") || this.wrapZipInput(zipInput);
    const errorEl = zipWrapper?.querySelector(".product-addons__zip-error");
    const searchBtn = zipWrapper?.querySelector("[data-zip-search]");
    let zipcodesLoaded = false;
    let productRulesLoaded = false;

    // Lazy load zipcodes and product rules on first focus
    const ensureDataLoaded = async () => {
      if (!zipcodesLoaded) {
        zipcodesLoaded = true;
        await Promise.all([this.getZipcodes(), this.getZipcodes(true), this.getZipcodesWa()]);
      }
      if (!productRulesLoaded) {
        productRulesLoaded = true;
        await this.getProductRules();
      }
    };

    zipInput.addEventListener("focus", ensureDataLoaded);

    const validateZipCode = async () => {
      let zipCode = zipInput.value.trim();
      this.hideZipError(zipWrapper);

      if (!zipCode) {
        this.showZipError(zipWrapper, errorEl, this.dataset.zipRequiredMessage);
        return;
      }
      // US ZIPs with a leading 0 are often entered as 4 digits (e.g. 7301 → 07301)
      if (/^\d{4}$/.test(zipCode)) {
        zipCode = `0${zipCode}`;
        zipInput.value = zipCode;
      }
      if (zipCode.length < 5) {
        this.showZipError(zipWrapper, errorEl, this.dataset.zipTooShortMessage);
        return;
      }
      if (/[a-zA-Z]/.test(zipCode)) {
        this.showZipError(zipWrapper, errorEl, this.dataset.zipInvalidMessage);
        return;
      }

      // Ensure zipcodes and product rules are loaded before validating
      await ensureDataLoaded();

      const zipCodes = await this.getZipcodes();
      if (!zipCodes) {
        console.error("[ProductAddons] Failed to load zipcodes");
        return;
      }

      const zipCodesLocal = await this.getZipcodes(true);
      if (!zipCodesLocal) {
        console.error("[ProductAddons] Failed to load zipcodes local");
        return;
      }

      const localZipCode = zipCodesLocal[zipCode];
      if (localZipCode) {
        this.showZipConfirmed(zipWrapper, zipInput, zipCode, localZipCode);
        this.loadServicesAfterZip(zipCode, true);
        return;
      }

      const zipCodesWa = await this.getZipcodesWa();
      const waLocation = zipCodesWa?.zipcodes?.[zipCode];
      if (waLocation) {
        this.showZipConfirmed(zipWrapper, zipInput, zipCode, waLocation);
        this.loadServicesAfterZip(zipCode, false, zipCodesWa.ignore_installation_products);
        return;
      }

      const location = typeof zipCodes === "object" && !Array.isArray(zipCodes)
        ? zipCodes[zipCode]
        : null;

      if (!location) {
        this.showZipError(zipWrapper, errorEl, this.dataset.zipUnsupportedMessage);
        return;
      }

      this.showZipConfirmed(zipWrapper, zipInput, zipCode, location);
      this.loadServicesAfterZip(zipCode);
    };

    // Handle Enter key
    zipInput.addEventListener("keydown", async (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      await validateZipCode();
    });

    // Handle search button click
    if (searchBtn) {
      searchBtn.addEventListener("click", async (event) => {
        event.preventDefault();
        await validateZipCode();
      });
    }
  }

  wrapZipInput(zipInput) {
    const wrapper = document.createElement("div");
    wrapper.className = "product-addons__zip-wrapper";
    zipInput.parentNode.insertBefore(wrapper, zipInput);
    wrapper.appendChild(zipInput);
    const errorEl = document.createElement("span");
    errorEl.className = "product-addons__zip-error";
    errorEl.setAttribute("role", "alert");
    errorEl.hidden = true;
    wrapper.appendChild(errorEl);
    return wrapper;
  }

  showZipError(zipWrapper, errorEl, message) {
    const el = errorEl || zipWrapper?.querySelector(".product-addons__zip-error");
    if (!el) return;
    const msg = message || this.dataset.zipUnsupportedMessage || "We do not support this zip code.";
    el.textContent = msg;
    el.hidden = false;
  }

  hideZipError(zipWrapper) {
    const el = zipWrapper?.querySelector(".product-addons__zip-error");
    if (el) el.hidden = true;
  }

  showZipConfirmed(zipWrapper, zipInput, zipCode, location) {
    this.hideZipError(zipWrapper);

    const zipInputGroup = zipInput.closest(".product-addons__zip-input-group");
    const searchBtn = zipInputGroup?.querySelector("[data-zip-search]");
    const container = zipInputGroup?.parentNode || zipInput.parentNode;
    const anchorNode = zipInputGroup || zipInput;
    if (!container || !anchorNode) return;

    const confirmedGroup = document.createElement("div");
    confirmedGroup.className = "product-addons__zip-input-group product-addons__zip-confirmed-group";
    const confirmed = document.createElement("div");
    confirmed.className = "product-addons__zip-confirmed";
    const locationSpan = document.createElement("span");
    locationSpan.className = "product-addons__zip-location";
    locationSpan.textContent = location;
    const changeBtn = document.createElement("button");
    changeBtn.type = "button";
    changeBtn.className = "btn btn--primary product-addons__zip-search product-addons__zip-change";
    changeBtn.setAttribute("aria-label", "Change zip code");
    changeBtn.textContent = "Change";
    confirmed.appendChild(locationSpan);
    confirmedGroup.appendChild(confirmed);
    confirmedGroup.appendChild(changeBtn);

    const hiddenInput = document.createElement("input");
    hiddenInput.type = "hidden";
    hiddenInput.name = zipInput.name || "properties[Zip Code]";
    hiddenInput.value = zipCode;

    zipInput.hidden = true;
    zipInput.value = "";
    if (zipInputGroup) {
      zipInputGroup.hidden = true;
    }
    if (searchBtn) {
      searchBtn.hidden = true;
    }
    container.insertBefore(confirmedGroup, anchorNode);
    container.insertBefore(hiddenInput, anchorNode);

    changeBtn.addEventListener("click", () => {
      confirmedGroup.remove();
      hiddenInput.remove();
      if (zipInputGroup) {
        zipInputGroup.hidden = false;
      }
      if (searchBtn) {
        searchBtn.hidden = false;
      }
      zipInput.hidden = false;
      zipInput.value = zipCode;
      zipInput.focus();
      this.hideZipError(zipWrapper);
      this.hideServices();
    });
  }

  hideServices() {
    const productAddonsContent = this.querySelector(".product-addons__content");
    const serviceWrapper = this.querySelector(".product-addons__services");
    if (productAddonsContent) productAddonsContent.style.display = "none";
    if (serviceWrapper) serviceWrapper.innerHTML = "";
  }

  async loadServicesAfterZip(zipCode, isLocal = false, ignoreProductIds = []) {
    // Ensure product rules are loaded
    const rules = await this.getProductRules(isLocal);
    if (!rules) {
      this.showNoServicesMessage();
      return false;
    }

    const subCategoryName = this.product?.metafields?.custom?.sub_category_name;
    if (!subCategoryName) {
      this.showNoServicesMessage();
      return false;
    }

    console.log("subCategoryName", subCategoryName);

    let filteredRules = rules.filter((rule) => rule.category_name === subCategoryName);
    if (!filteredRules || filteredRules.length === 0) {
      this.showNoServicesMessage();
      return false;
    }
    console.log("filteredRules", filteredRules);

    // Filter rules by product tags (case-insensitive)
    // Convert product tags to lowercase for comparison
    const isProItem = this.product?.metafields?.custom?.pro_item === "YES";

    if (isProItem) {
      // Filter rules where ALL tags in the rule are present in the product's tags
      filteredRules = filteredRules.filter((rule) => {
        if (!rule.tags || rule.tags.length === 0) {
          return true; // Include rules with no tag requirements
        }

        // Check if all rule tags exist in product tags (case-insensitive)
        return rule.tags.includes("Pro");
      });

      if (!filteredRules || filteredRules.length === 0) {
        this.showNoServicesMessage();
        return false;
      }
    }

    const matchedRule = this._findMatchingRule(filteredRules, this.product);
    if (!matchedRule) {
      this.showNoServicesMessage();
      return false;
    }

    console.log("matchedRule", matchedRule);

    let productIds = matchedRule.products || [];
    if (ignoreProductIds?.length) {
      const ignoreSet = new Set(ignoreProductIds.map((id) => String(id)));
      productIds = productIds.filter((id) => !ignoreSet.has(String(id)));
    }

    if (productIds.length === 0) {
      this.showNoServicesMessage();
      return false;
    }

    const products = await this.fetchProducts(productIds);
    this.loadServices(products);
    return true;
  }

  showNoServicesMessage() {
    const serviceWrapper = this.querySelector(".product-addons__services");
    const productAddonsContent = this.querySelector(".product-addons__content");

    if (!serviceWrapper || !productAddonsContent) return;

    serviceWrapper.innerHTML = "";
    const noServicesMessage = this.dataset.noServicesMessage || "No services available for this zip code.";
    const messageEl = document.createElement("div");
    messageEl.className = "alert bg-error-bg text-error-text mt-6 product-addons__no-services";
    messageEl.setAttribute("role", "alert");
    messageEl.textContent = noServicesMessage;
    serviceWrapper.appendChild(messageEl);
    productAddonsContent.style.display = "block";
  }

  loadServices(products) {
    const services = products
      .map((product) => {
        const variants = product.variants?.edges ?? [];
        const availableVariant = variants.find((e) => e?.node?.availableForSale);
        const variantId = availableVariant?.node?.id
          ? String(availableVariant.node.id).replace("gid://shopify/ProductVariant/", "")
          : null;
        const price = availableVariant?.node?.price?.amount ?? 0;
        return variantId
          ? {
              id: product.id,
              variantId,
              title: product.title,
              description: product.description,
              price: price,
            }
          : null;
      })
      .filter(Boolean);

    this.generateServices(services);
  }

  positionServiceTooltip(infoEl) {
    if (!infoEl || window.innerWidth < 769) return;

    const tooltip = infoEl.querySelector(".product-addons__service-tooltip");
    if (!tooltip) return;

    const spacing = 8;
    const viewportPadding = 12;
    const infoRect = infoEl.getBoundingClientRect();

    // Reset to a measurable baseline before calculating clamped viewport position.
    tooltip.style.left = "0px";
    tooltip.style.top = "0px";
    tooltip.style.right = "auto";
    tooltip.style.bottom = "auto";

    const tooltipRect = tooltip.getBoundingClientRect();
    let left = infoRect.left + (infoRect.width / 2) - (tooltipRect.width / 2);
    left = Math.max(viewportPadding, Math.min(left, window.innerWidth - tooltipRect.width - viewportPadding));

    let top = infoRect.top - tooltipRect.height - spacing;
    if (top < viewportPadding) {
      top = infoRect.bottom + spacing;
    }

    tooltip.style.left = `${Math.round(left)}px`;
    tooltip.style.top = `${Math.round(top)}px`;
  }

  generateServices(services) {
    const serviceWrapper = this.querySelector(".product-addons__services");
    if (!serviceWrapper) return;

    const productAddonsContent = this.querySelector(".product-addons__content");
    if (!productAddonsContent) return;

    // Clear any previous content (including no-services message)
    serviceWrapper.innerHTML = "";

    // If no services available, show message
    if (!services || services.length === 0) {
      const noServicesMessage = this.dataset.noServicesMessage || "No services available for this zip code.";
      const messageEl = document.createElement("div");
      messageEl.className = "alert bg-error-bg text-error-text mt-6 product-addons__no-services";
      messageEl.setAttribute("role", "alert");
      messageEl.textContent = noServicesMessage;
      serviceWrapper.appendChild(messageEl);
      productAddonsContent.style.display = "block";
      return;
    }

    // Services available, show them
    productAddonsContent.style.display = "block";

    services.forEach((service, index) => {
      if (!service.variantId) return;
      const formattedPrice = this.formatServicePrice(service.price);
      const safeTitle = this.escapeHtml(service.title || "");
      const isFirstService = index === 0;
      const isSecondService = index === 1;
      const hasDesktopShortLabel = isFirstService || isSecondService;
      const desktopShortLabel = isSecondService
        ? "Haul-away"
        : this.getFirstServiceDesktopLabel(service.title);
      const serviceElement = document.createElement("div");
      serviceElement.classList.add("product-addons__service");
      if (hasDesktopShortLabel) {
        serviceElement.classList.add("product-addons__service--desktop-short");
      }
      serviceElement.dataset.variantId = service.variantId;
      serviceElement.dataset.price = service.price || 0;
      serviceElement.innerHTML = `
        <input type="checkbox" hidden id="product-addons-service-${service.id}" name="product-addons-service" value="${service.variantId}">
        <div class="product-addons__service-checkbox">
          <div class="product-addons__service-checkbox-inner"></div>
        </div>
        <label class="product-addons__service-label" for="product-addons-service-${service.id}">
          <span class="product-addons__service-title">${safeTitle}</span>
          ${hasDesktopShortLabel ? `
            <span class="product-addons__service-short-desktop">${this.escapeHtml(desktopShortLabel)}</span>
          ` : ""}
          <span class="product-addons__service-price">+${formattedPrice}</span>
          ${hasDesktopShortLabel ? `
            <span class="product-addons__service-info" tabindex="0" role="button" aria-label="Show service details">
              <svg class="product-addons__service-info-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"></circle>
                <path d="M8 7V11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path>
                <circle cx="8" cy="4.75" r="0.75" fill="currentColor"></circle>
              </svg>
              <span class="product-addons__service-tooltip">${safeTitle}</span>
            </span>
          ` : ""}
        </label>
      `;
      serviceWrapper.appendChild(serviceElement);

      const infoEl = serviceElement.querySelector(".product-addons__service-info");
      if (infoEl) {
        const updateTooltipPosition = () => this.positionServiceTooltip(infoEl);
        infoEl.addEventListener("mouseenter", updateTooltipPosition);
        infoEl.addEventListener("focus", updateTooltipPosition);
        infoEl.addEventListener("click", updateTooltipPosition);
      }

      const checkbox = serviceElement.querySelector('input[type="checkbox"]');
      const syncState = () => {
        serviceElement.classList.toggle("checked", checkbox.checked);
        this.updateTotalPrice();
      };
      serviceElement.querySelector(".product-addons__service-checkbox").addEventListener("click", () => {
        checkbox.checked = !checkbox.checked;
        syncState();
      });
      checkbox.addEventListener("change", syncState);
    });

    this.updateTotalPrice();
  }

  formatServicePrice(price) {
    const amount = Number(price) || 0;
    return amount.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  getFirstServiceDesktopLabel(title) {
    const words = String(title || "").trim().split(/\s+/).filter(Boolean);
    const installationWordIndex = words.findIndex((word) => {
      const normalized = word.toLowerCase().replace(/[^a-z]/g, "");
      return normalized === "installation" || normalized === "installations";
    });

    // If "installation/installations" appears within first 5 words, keep from start to that word.
    if (installationWordIndex !== -1 && installationWordIndex < 5) {
      return words.slice(0, installationWordIndex + 1).join(" ");
    }

    // Fallback for missing or late installation word: first 5 words + ellipsis.
    if (words.length <= 5) {
      return words.join(" ");
    }

    return `${words.slice(0, 5).join(" ")}...`;
  }

  updateTotalPrice() {
    const serviceElements = this.querySelectorAll(".product-addons__service");
    const totalEl = this.querySelector(".product-addons__total");
    const priceEl = this.querySelector(".product-addons__total-price");

    if (!totalEl || !priceEl) return;

    let totalPrice = 0;
    serviceElements.forEach((el) => {
      const checkbox = el.querySelector('input[type="checkbox"]');
      if (checkbox && checkbox.checked) {
        const price = parseFloat(el.dataset.price) || 0;
        totalPrice += price;
      }
    });

    if (totalPrice > 0) {
      priceEl.textContent = `$${totalPrice.toFixed(2)}`;
      totalEl.hidden = false;
    } else {
      totalEl.hidden = true;
    }
  }

  getSelectedAddonVariantIds() {
    const checked = this.querySelectorAll(
      "input[name='product-addons-service']:checked"
    );
    const ids = Array.from(checked).map((el) => el.value).filter(Boolean);
    const labels = Array.from(checked).map(
      (el) => el.closest(".product-addons__service")?.querySelector(".product-addons__service-label")?.textContent
    );
    return ids;
  }

  /**
   * Get the main product variant ID from the product form
   * @returns {string | null} Variant ID or null if not found
   */
  getMainProductVariantId() {
    const section = this.closest("[id^='shopify-section']");
    const productForm = section?.querySelector(".js-product-form-main") ??
                       this.closest(".product-info")?.querySelector(".js-product-form-main");

    if (!productForm) {
      return null;
    }

    // Look for variant input in form
    const variantInput = productForm.querySelector('[name="id"]');
    if (variantInput?.value) {
      return variantInput.value;
    }

    // Fallback: try data attribute
    return productForm.dataset.variantId || null;
  }

  uncheckAddonByVariantId(variantId) {
    const input = this.querySelector(
      `input[name="product-addons-service"][value="${variantId}"]`
    );
    if (input) {
      input.checked = false;
      input.closest(".product-addons__service")?.classList.remove("checked");
    }
  }

  initAddToCartListener() {
    const section = this.closest("[id^='shopify-section']");
    const productForm = section?.querySelector(".js-product-form-main") ?? this.closest(".product-info")?.querySelector(".js-product-form-main");
    if (!productForm) return;

    productForm.addEventListener(
      "submit",
      (event) => {
        const variantIds = this.getSelectedAddonVariantIds();
        if (variantIds.length) {
          // Always prevent default and handle manually when service addons are selected
          event.preventDefault();
          event.stopPropagation();
          this.handleFormSubmitWithAddons(event.target, variantIds);
        }
      },
      { capture: true }
    );
  }

  async handleFormSubmitWithAddons(form, variantIds) {

    // Extract quantity from form first
    const formData = new FormData(form);
    const mainProductQuantity = parseInt(formData.get("quantity") || "1", 10);

    // Add ALL service variants in a single request via Ajax API
    const baseUrl = theme?.routes?.cartAdd || (window.Shopify?.routes?.root || "/") + "cart/add";
    const cartAddUrl = baseUrl.replace(/\/cart\/add\/?$/, "/cart/add.js");

    // Track added service variants so we can remove them if main add fails
    const addedServiceVariantIds = [];

    // Build payload with all service variants at once - use same quantity as main product
    const serviceItems = variantIds.map(variantId => ({
      id: parseInt(variantId, 10),
      quantity: mainProductQuantity
    }));


    try {
      const payload = { items: serviceItems };

      const response = await fetch(cartAddUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        console.error("[ProductAddons] Failed to add service variants:", response.statusText);
        // Uncheck all on failure
        variantIds.forEach(variantId => this.uncheckAddonByVariantId(variantId));
      } else {
        // All service variants were added successfully
        addedServiceVariantIds.push(...variantIds);
      }
    } catch (error) {
      console.error("[ProductAddons] Error adding service variants:", error);
      // Uncheck all on error
      variantIds.forEach(variantId => this.uncheckAddonByVariantId(variantId));
    }


    // Submit the form via Ajax (to avoid redirect)
    try {
      const formDataForMain = new FormData(form);

      // Extract only the product id and quantity, ignore form metadata
      const variantId = parseInt(formDataForMain.get("id"), 10);
      const quantity = mainProductQuantity; // Use the quantity we already extracted

      // Build properties object - only include fields that start with "properties["
      const properties = {};
      for (const [key, value] of formDataForMain.entries()) {
        if (key.startsWith("properties[")) {
          const propName = key.replace("properties[", "").replace("]", "");
          properties[propName] = value;
        }
      }

      const payload = {
        items: [{
          id: variantId,
          quantity: quantity
        }]
      };

      // Only add properties if there are any
      if (Object.keys(properties).length > 0) {
        payload.items[0].properties = properties;
      }

      // Store mapping of main variant to service variants for removal later
      if (addedServiceVariantIds.length > 0) {
        const serviceMapKey = `_serviceVariants_${variantId}`;
        localStorage.setItem(serviceMapKey, JSON.stringify(addedServiceVariantIds));

        // Also store reverse mapping: service variant -> main variant (for cart page quantity sync)
        for (const serviceVariantId of addedServiceVariantIds) {
          const reverseMapKey = `_mainVariant_${serviceVariantId}`;
          localStorage.setItem(reverseMapKey, variantId.toString());
        }
      }


      const response = await fetch(cartAddUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        console.error("[ProductAddons] Failed to add main product:", response.statusText);

        // Main product failed - remove service variants that were already added
        if (addedServiceVariantIds.length > 0) {
          await this.removeCartItems(addedServiceVariantIds);
        }

        // Uncheck service addons on failure
        variantIds.forEach(variantId => this.uncheckAddonByVariantId(variantId));

        // Form submission failed - don't submit
        return;
      }


      // Open and refresh cart drawer
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer?.refresh) {
        cartDrawer.refresh(true);
      } else {
        document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh"));
      }

      // Open the drawer
      document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:open"));
    } catch (error) {
      console.error("[ProductAddons] Error submitting form:", error);

      // On error, remove service variants that were added
      if (addedServiceVariantIds.length > 0) {
        await this.removeCartItems(addedServiceVariantIds);
      }

      // Uncheck service addons on error
      variantIds.forEach(variantId => this.uncheckAddonByVariantId(variantId));
    }
  }


  async fetchProducts(productIds) {
    const apiUrl = this.dataset.storefrontApiUrl;
    const token = this.dataset.storefrontApiToken;


    if (!apiUrl || !token) {
      console.error("Storefront API URL and token are required");
      return [];
    }

    const ids = (Array.isArray(productIds) ? productIds : [productIds])
      .map((id) =>
        typeof id === "string" && id.startsWith("gid://")
          ? id
          : `gid://shopify/Product/${id}`
      );


    if (ids.length === 0) return [];

    const query = `
      query getProducts($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on Product {
            id
            title
            handle
            vendor
            productType
            availableForSale
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            featuredImage {
              url
              altText
              width
              height
            }
            variants(first: 10) {
              edges {
                node {
                  id
                  title
                  availableForSale
                  price {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": token,
        },
        body: JSON.stringify({ query, variables: { ids } }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const { data, errors } = await response.json();

      if (errors) {
        console.error("[ProductAddons] Storefront API errors:", errors);
        return [];
      }

      const products = (data?.nodes ?? []).filter(Boolean);
      return products;
    } catch (error) {
      console.error("Error fetching products:", error);
      return [];
    }
  }

  /**
   * Fetches product variants by ID from the Shopify Storefront API.
   * Returns the parent product for each variant.
   * @param {string[]|number[]} variantIds - Array of variant IDs (numeric or GID format)
   * @returns {Promise<Object[]>} Array of product objects (from variant.product)
   */
  async fetchProductsByVariantIds(variantIds) {
    const apiUrl = this.dataset.storefrontApiUrl;
    const token = this.dataset.storefrontApiToken;

    if (!apiUrl || !token) {
      console.error("Storefront API URL and token are required");
      return [];
    }

    const ids = (Array.isArray(variantIds) ? variantIds : [variantIds])
      .map((id) =>
        typeof id === "string" && id.startsWith("gid://")
          ? id
          : `gid://shopify/ProductVariant/${id}`
      );

    if (ids.length === 0) return [];

    const query = `
      query getVariants($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on ProductVariant {
            id
            product {
              id
              title
              handle
              vendor
              availableForSale
              priceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
              featuredImage {
                url
                altText
              }
              variants(first: 10) {
                edges {
                  node {
                    id
                    title
                    availableForSale
                    price {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": token,
        },
        body: JSON.stringify({ query, variables: { ids } }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const { data, errors } = await response.json();
      if (errors) {
        console.error("Storefront API errors:", errors);
        return [];
      }

      const nodes = data?.nodes ?? [];
      return nodes.filter((n) => n?.product).map((n) => n.product);
    } catch (error) {
      console.error("Error fetching products by variant IDs:", error);
      return [];
    }
  }

  shouldBeHidden() {
    const isServiceAvailable = this.product?.available;
    const isCallForPrice = this.product?.metafields?.custom?.call_for_price === '1';
    const isOutOfStock = this.product?.metafields?.custom?.stock_status === 'Out of Stock';

    return !isServiceAvailable && isCallForPrice && isOutOfStock;
  }

  connectedCallback() {
    if (this.shouldBeHidden()) {
      this.style.display = 'none';
      return;
    }

    this.initZipCodeInput();
    this.initAddToCartListener();
    this.initCartRemovalListener();
    this.initQuantityChangeListener();
    this.initDOMQuantityWatcher();

    if (this.product?.metafields) {
    }

    // Fetch detailed product information including tags and metafields from Storefront API
    this.fetchMainProductDetails().catch((error) => {
      console.error("[ProductAddons] Failed to fetch main product details:", error);
    });
  }

  initQuantityChangeListener() {
    // Handler function for quantity change events
    const handleQuantityChange = (event) => {
      const { variantId, newQuantity } = event.detail;

      // Check if this variant has linked service products
      const serviceMapKey = `_serviceVariants_${variantId}`;

      const serviceVariantsJson = localStorage.getItem(serviceMapKey);

      if (serviceVariantsJson) {
        try {
          const serviceVariants = JSON.parse(serviceVariantsJson);

          if (newQuantity === 0) {
            // Main product was removed, remove services too
            this.removeCartItems(serviceVariants);
          } else {
            // Update service quantities to match main product
            this.updateServiceQuantities(serviceVariants, newQuantity);
          }
        } catch (error) {
          console.error("[ProductAddons] Error handling quantity change:", error);
        }
      } else {
        // Try as string
        const strKey = `_serviceVariants_${variantId.toString()}`;
        const strValue = localStorage.getItem(strKey);
      }
    };


    // Listen on document (for bubbled events)
    document.addEventListener("on:line-item:change", handleQuantityChange);

    // Also listen on cart-items element directly (for both minicart and cart page)
    const cartItems = document.querySelector("cart-items");
    if (cartItems) {
      cartItems.addEventListener("on:line-item:change", handleQuantityChange);
    } else {
    }

    // Use MutationObserver to detect when cart-items is added to the page
    const observer = new MutationObserver(() => {
      const cartItems = document.querySelector("cart-items");
      if (cartItems && !cartItems._productAddonsListenerAttached) {
        cartItems._productAddonsListenerAttached = true;
        cartItems.addEventListener("on:line-item:change", handleQuantityChange);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });


    // Also retry on DOMContentLoaded
    document.addEventListener("DOMContentLoaded", () => {
      const cartItemsRetry = document.querySelector("cart-items");
      if (cartItemsRetry && !cartItemsRetry._productAddonsListenerAttached) {
        cartItemsRetry._productAddonsListenerAttached = true;
        cartItemsRetry.addEventListener("on:line-item:change", handleQuantityChange);
      }
    });
  }

  initDOMQuantityWatcher() {

    // Track pending updates per variant to debounce
    const debounceTimers = new Map();

    document.addEventListener("change", (event) => {
      const quantityInput = event.target.closest("quantity-input");
      if (!quantityInput) return;

      const input = quantityInput.querySelector("input[type='number']");
      const cartItem = quantityInput.closest(".cart-item");
      if (!input || !cartItem) return;

      const variantId = cartItem.dataset.variantId;
      if (!variantId) return;


      // Cancel previous debounce for this variant
      if (debounceTimers.has(variantId)) {
        clearTimeout(debounceTimers.get(variantId));
      }

      // Debounce by 2 seconds to let user finish changing quantity and to spread out API calls
      const timer = setTimeout(() => {
        debounceTimers.delete(variantId);

        // READ THE CURRENT QUANTITY VALUE NOW, not from earlier
        const currentQuantity = parseInt(input.value, 10);

        const serviceMapKey = `_serviceVariants_${variantId}`;
        const serviceVariantsJson = localStorage.getItem(serviceMapKey);
        if (!serviceVariantsJson) return;

        try {
          const serviceVariants = JSON.parse(serviceVariantsJson);

          if (currentQuantity === 0) {
            this.showPreloader();
            this.removeCartItems(serviceVariants).then(() => {
              this.hidePreloader();
            });
          } else {
            this.showPreloader();
            this.updateServiceQuantities(serviceVariants, currentQuantity);
          }
        } catch (error) {
          console.error("[ProductAddons] Error updating services:", error);
          this.hidePreloader();
        }
      }, 2000);

      debounceTimers.set(variantId, timer);
    }, true);

  }

  async updateServiceQuantities(serviceVariantIds, newQuantity) {

    const baseUrl = theme?.routes?.cartChange || (window.Shopify?.routes?.root || "/") + "cart/change";

    // Wait for cart DOM to fully update after main product change
    await new Promise(resolve => setTimeout(resolve, 500));

    // Update each service sequentially with SHORT delays (Shopify only supports single-line updates)
    for (let i = 0; i < serviceVariantIds.length; i++) {
      const variantId = serviceVariantIds[i];
      try {
        const variantIdNum = parseInt(variantId, 10);

        // Get fresh cart items before each update
        const cartItems = document.querySelectorAll(".cart-item");
        let lineIndex = null;

        for (let j = 0; j < cartItems.length; j++) {
          if (parseInt(cartItems[j].dataset.variantId, 10) === variantIdNum) {
            lineIndex = j + 1; // Shopify uses 1-indexed line numbers
            break;
          }
        }

        if (!lineIndex) {
          continue;
        }


        // Make API call to update quantity
        const response = await fetch(baseUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ line: lineIndex, quantity: newQuantity })
        });

        if (!response.ok) {
          console.error("[ProductAddons] Failed to update service:", response.status);
        }

        // Short delay between updates: 300ms (Shopify can handle this)
        // For last service, don't wait
        if (i < serviceVariantIds.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      } catch (error) {
        console.error("[ProductAddons] Error updating service:", error.message);
      }
    }

    // Refresh cart display after all updates
    setTimeout(() => {
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer?.refresh) {
        cartDrawer.refresh(true);
      }
      const cartItems = document.querySelector("cart-items");
      if (cartItems?.refresh) {
        cartItems.refresh(true);
      }

      // Hide preloader after cart refresh completes
      this.hidePreloader();
    }, 300);
  }

  initCartRemovalListener() {
    // Listen for cart item removals and handle service product cleanup
    document.addEventListener("click", async (event) => {
      const removeBtn = event.target.closest(".js-remove-item");
      if (!removeBtn) return;

      const cartItem = removeBtn.closest(".cart-item");
      if (!cartItem) return;

      const variantId = cartItem.dataset.variantId;
      if (!variantId) return;


      // Check if this is a main product that has associated service products
      const serviceMapKey = `_serviceVariants_${variantId}`;
      const serviceVariantsJson = localStorage.getItem(serviceMapKey);

      if (serviceVariantsJson) {
        try {
          // PREVENT the default click to avoid race condition
          event.preventDefault();
          event.stopImmediatePropagation();

          const serviceVariants = JSON.parse(serviceVariantsJson);

          // Show notification that services are being removed
          this.showPreloader("Removing services...");

          // Remove services FIRST
          await this.removeServiceVariants(serviceVariants);

          // Wait a bit for services to be removed from cart
          await new Promise(resolve => setTimeout(resolve, 500));

          // THEN remove the main product using the API (no redirect)
          await this.removeMainProductByApi(variantId, cartItem);

          this.hidePreloader();

          // Clean up the mapping
          localStorage.removeItem(serviceMapKey);
        } catch (error) {
          console.error("[ProductAddons] Error in cart removal:", error);
          this.hidePreloader();
        }
      }
    }, true);
  }

  async removeMainProductByApi(variantId, cartItem) {
    const baseUrl = theme?.routes?.cartChange || (window.Shopify?.routes?.root || "/") + "cart/change";

    try {

      // Find the line number of the main product in the current cart
      const cartItems = document.querySelectorAll(".cart-item");
      let lineNumber = null;

      for (let i = 0; i < cartItems.length; i++) {
        const item = cartItems[i];
        if (parseInt(item.dataset.variantId, 10) === parseInt(variantId, 10)) {
          lineNumber = i + 1; // Shopify uses 1-indexed line numbers
          break;
        }
      }

      if (lineNumber === null) {
        return;
      }

      // Remove the product via API (no redirect)
      const payload = {
        line: lineNumber,
        quantity: 0,
        sections: []
      };

      const response = await fetch(baseUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {

        // Refresh cart display without redirect
        setTimeout(() => {
          const cartDrawer = document.querySelector("cart-drawer");
          if (cartDrawer?.refresh) {
            cartDrawer.refresh(true);
          } else {
            document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh"));
          }
          const cartItems = document.querySelector("cart-items");
          if (cartItems?.refresh) {
            cartItems.refresh(true);
          }
        }, 500);
      } else {
        console.error("[ProductAddons] Failed to remove main product:", response.statusText);
      }
    } catch (error) {
      console.error("[ProductAddons] Error removing main product:", error);
    }
  }

  async removeServiceVariants(serviceVariantIds) {

    // Wait a bit to ensure cart DOM has updated after main product removal
    await new Promise(resolve => setTimeout(resolve, 200));

    // Use the safe DOM-based removal method: find items and click their remove buttons
    for (const serviceVariantId of serviceVariantIds) {
      try {
        const serviceVariantIdNum = parseInt(serviceVariantId, 10);

        // Find the cart item with matching variant ID
        const cartItems = document.querySelectorAll(".cart-item");
        let foundItem = null;

        for (let i = 0; i < cartItems.length; i++) {
          const item = cartItems[i];
          const itemVariantId = parseInt(item.dataset.variantId, 10);

          if (itemVariantId === serviceVariantIdNum) {
            foundItem = item;
            break;
          }
        }

        if (!foundItem) {
          continue;
        }

        // Find and click the remove button
        const removeButton = foundItem.querySelector(".js-remove-item");
        if (removeButton) {
          removeButton.click();

          // Small delay between removals to avoid race conditions
          await new Promise(resolve => setTimeout(resolve, 300));
        } else {
        }
      } catch (error) {
        console.error("[ProductAddons] Error removing service variant:", error);
      }
    }

    // Refresh cart display after all removals
    setTimeout(() => {
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer?.refresh) {
        cartDrawer.refresh(true);
      } else {
        document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh"));
      }
      const cartItems = document.querySelector("cart-items");
      if (cartItems?.refresh) {
        cartItems.refresh(true);
      }
    }, 500);
  }

  _normalizeValue(val) {
    if (typeof val === 'number') return val;

    const isAllLetters = /^[a-zA-Z]+$/.test(val);
    if (isAllLetters) return val;
  
    let s = String(val).trim();
    s = s.replace(/^U_/i, '');
    s = s.replace(/"$/, '').trim();
  
    const mixedMatch = s.match(/^(\d+)\s+(\d+)\/(\d+)$/);
    if (mixedMatch) {
      const whole = parseInt(mixedMatch[1]);
      const num   = parseInt(mixedMatch[2]);
      const den   = parseInt(mixedMatch[3]);
      return whole + num / den;
    }
  
    const fracMatch = s.match(/^(\d+)\/(\d+)$/);
    if (fracMatch) {
      return parseInt(fracMatch[1]) / parseInt(fracMatch[2]);
    }
  
    const n = parseFloat(s);
    return isNaN(n) ? null : n;
  }
  
  _isSameValue(original, candidate) {
    const a = this._normalizeValue(original);
    const b = this._normalizeValue(candidate);

    if (a !== null && b !== null) {
      if (a === b) return true;
      if (typeof a === "number" && typeof b === "number") {
        return Math.abs(a - b) <= 0.9;
      }
    }

    // Text metafields (e.g. att_style=Laundry Center) are not numeric.
    const sa = String(original ?? "").trim().toLowerCase();
    const sb = String(candidate ?? "").trim().toLowerCase();
    return sa !== "" && sa === sb;
  }
  
  _parseMetafield(metafieldStr) {
    const [key, ...rest] = metafieldStr.split('=');
    return { key: key.trim(), value: rest.join('=').trim() };
  }
  
  _ruleMatchesProduct(rule, product) {
    // Check if all metafields in the rule match the product
    if (!rule.metafields || rule.metafields.length === 0) {
      return true; // Generic rule with no metafield requirements
    }

    return rule.metafields.every(metafieldStr => {
      const { key, value } = this._parseMetafield(metafieldStr);
      const productValue = product.metafields.custom[key];
      if (productValue === undefined || productValue === null) return false;
      return this._isSameValue(value, productValue);
    });
  }

  _scoreRule(rule, product) {
    // Score a rule based on specificity: metafields + tags
    // Higher score = more specific rule
    // This prevents generic rules from short-circuiting specific ones

    let metafieldScore = 0;
    let tagScore = 0;

    // Score metafields: all must match, score = number of matching metafields
    if (rule.metafields && rule.metafields.length > 0) {
      let matchingMetafields = 0;
      for (const metafieldStr of rule.metafields) {
        const { key, value } = this._parseMetafield(metafieldStr);

        const productValue = product.metafields.custom[key];
        if (productValue !== undefined && productValue !== null && this._isSameValue(value, productValue)) {
          matchingMetafields++;
        }
      }

      // If not all metafields match, this rule doesn't apply (-1 = invalid)
      if (matchingMetafields !== rule.metafields.length) {
        return -1;
      }
      metafieldScore = rule.metafields.length;
    }

    // Score tags: product tags must contain ALL rule tags (if rule has tags)
    if (rule.tags && rule.tags.length > 0) {
      const isProductPro = product.metafields?.custom?.pro_item === "YES";

      if (!isProductPro && rule.tags.includes("Pro")) {
        return -1; // Rule tags don't match product, invalid
      }

      // Score: 1000 + number of matching tags (prioritize tag matches)
      tagScore = 1000 + rule.tags.length;
    }

    // Total score: tag score (if applicable) + metafield score
    // Rules with matching tags score much higher than metafield-only rules
    return tagScore + metafieldScore;
  }

  _findMatchingRule(rules, product) {
    // Find the MOST SPECIFIC rule (highest score) instead of the first match
    // This makes the algorithm independent of rule order in the JSON

    let bestRule = null;
    let bestScore = -1;

    for (const rule of rules) {
      const score = this._scoreRule(rule, product);

      // Only consider rules that fully match (score >= 0)
      // Prefer specific rules (higher score) over generic rules (score 0)
      if (score > bestScore) {
        bestScore = score;
        bestRule = rule;
      }
    }

    return bestRule ?? null;
  }

  async removeCartItems(variantIds) {

    try {
      // Find and click remove buttons for each service variant
      for (const variantId of variantIds) {
        const variantIdNum = parseInt(variantId, 10);

        // Find cart item with this variant ID
        const cartItems = document.querySelectorAll(".cart-item");
        let foundItem = null;

        for (let i = 0; i < cartItems.length; i++) {
          const item = cartItems[i];
          const itemVariantId = parseInt(item.dataset.variantId, 10);

          if (itemVariantId === variantIdNum) {
            foundItem = item;
            break;
          }
        }

        if (foundItem) {
          // Find and click the remove button
          const removeButton = foundItem.querySelector(".js-remove-item");

          if (removeButton) {
            removeButton.click();
          } else {
            // Try alternative selectors
            const altButton = foundItem.querySelector("[data-remove], button[aria-label*='Remove'], .cart-item__remove");
            if (altButton) {
              altButton.click();
            }
          }
        } else {
        }
      }
    } catch (error) {
      console.error("[ProductAddons] Error removing cart items:", error);
      console.error("[ProductAddons] Error stack:", error.stack);
    }
  }

  showPreloader(message = "Updating services...") {

    // Check if notification already exists
    const existing = document.querySelector("#product-addons-notification");
    if (existing) {
      existing.style.display = "flex";
      // Update message if provided
      const textSpan = existing.querySelector("[data-notification-text]");
      if (textSpan) {
        textSpan.textContent = message;
      }
      return;
    }


    // Create notification container
    const notification = document.createElement("div");
    notification.id = "product-addons-notification";
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #f0f0f0;
      border: 1px solid #ddd;
      border-radius: 8px;
      padding: 12px 16px;
      display: flex;
      align-items: center;
      gap: 10px;
      z-index: 99999;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      font-size: 14px;
      color: #333;
    `;

    // Create spinner
    const spinner = document.createElement("div");
    spinner.style.cssText = `
      width: 16px;
      height: 16px;
      border: 2px solid #e0e0e0;
      border-top: 2px solid #333;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
      flex-shrink: 0;
    `;

    // Create text
    const text = document.createElement("span");
    text.setAttribute("data-notification-text", "true");
    text.textContent = message;

    // Add animation (check if it already exists to avoid duplicate styles)
    if (!document.querySelector("style[data-spinner-animation]")) {
      const style = document.createElement("style");
      style.setAttribute("data-spinner-animation", "true");
      style.textContent = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }

    notification.appendChild(spinner);
    notification.appendChild(text);
    document.body.appendChild(notification);
  }

  hidePreloader() {

    const notification = document.querySelector("#product-addons-notification");
    if (notification) {
      notification.remove();
    }
  }
}

customElements.define("product-addons", ProductAddons);
